import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import PublicCompliance from "@/pages/PublicCompliance";

// Lazy-loaded secondary sections — navigation between menus is instant on
// repeat visits because the chunks are cached after first load.
const LiquidityMarketplace = lazy(() => import("@/pages/LiquidityMarketplace"));
const ArbitrageEnginePage = lazy(() => import("@/pages/ArbitrageEnginePage"));

function PageFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "var(--mq-bg)" }}
      data-testid="page-loading-fallback"
    >
      <span className="mq-label">Loading…</span>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--mq-bg)" }}>
        <span className="mq-label">Initialising…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--mq-bg)" }}>
        <span className="mq-label">Initialising…</span>
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route path="/compliance" element={<PublicCompliance />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/liquidity"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageFallback />}>
                    <LiquidityMarketplace />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/arbitrage"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageFallback />}>
                    <ArbitrageEnginePage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </I18nProvider>
  );
}
